/**
 * Form field: label, control, description, error (CLAUDE.md 14.5).
 *
 * The accessibility contract this exists to guarantee, which was the gap in the
 * hand-rolled form it replaces:
 *
 * - the label is programmatically associated with the control, not merely adjacent;
 * - the description and the error are both referenced by `aria-describedby`, so a
 *   screen-reader user hears *why* a field is invalid rather than just that it is;
 * - `aria-invalid` marks the control itself, so the error is not signalled by colour
 *   alone (14.2).
 *
 * Ids are generated with `useId` so a field can appear many times on a page -- a span
 * table has one of these per row.
 */
import { createContext, useContext, useId, type ReactNode } from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { AlertCircle } from 'lucide-react'

import { cn } from '@/lib/cn'

interface FieldContextValue {
  id: string
  descriptionId: string
  errorId: string
  hasError: boolean
  hasDescription: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

/**
 * The surrounding field, for the label and error subcomponents.
 *
 * These genuinely require a `<Field>`: a label with nothing to point at is a bug.
 */
export function useField(): FieldContextValue {
  const context = useContext(FieldContext)
  if (!context) throw new Error('Field subcomponents must be used inside <Field>')
  return context
}

/**
 * Props to spread onto the control. Returns the wiring, not the markup, so a field
 * can wrap a plain input, a Radix Select, or a bespoke section editor equally.
 */
export function useFieldControlProps() {
  // A *control* does not require a Field. `NumberInput` is used inside one on a module
  // form, and bare in a table cell or a toolbar; making the wrapper mandatory would
  // force a pointless Field around every one of those, or a second component. When
  // there is no Field, the control simply carries whatever labelling its caller gave
  // it -- an `aria-label`, usually.
  const context = useContext(FieldContext)
  if (!context) return {} as const

  const { id, descriptionId, errorId, hasError, hasDescription } = context
  const describedBy = [hasDescription ? descriptionId : null, hasError ? errorId : null]
    .filter(Boolean)
    .join(' ')

  return {
    id,
    'aria-invalid': hasError || undefined,
    'aria-describedby': describedBy || undefined,
  } as const
}

export function Field({
  children,
  error,
  description,
  className,
}: {
  children: ReactNode
  error?: string | null
  description?: ReactNode
  className?: string
}) {
  const id = useId()
  const value: FieldContextValue = {
    id,
    descriptionId: `${id}-description`,
    errorId: `${id}-error`,
    hasError: Boolean(error),
    hasDescription: Boolean(description),
  }

  return (
    <FieldContext.Provider value={value}>
      <div className={cn('space-y-1.5', className)}>
        {children}
        {description && !error && <FieldDescription>{description}</FieldDescription>}
        {error && <FieldError>{error}</FieldError>}
      </div>
    </FieldContext.Provider>
  )
}

export function FieldLabel({
  children,
  className,
  hint,
}: {
  children: ReactNode
  className?: string
  /** Unit symbol or similar. Never translated (14.4). */
  hint?: ReactNode
}) {
  const { id } = useField()
  return (
    <LabelPrimitive.Root
      htmlFor={id}
      className={cn('flex items-baseline justify-between gap-2 text-sm font-medium', className)}
    >
      <span>{children}</span>
      {hint && <span className="text-xs font-normal text-text-muted">{hint}</span>}
    </LabelPrimitive.Root>
  )
}

export function FieldDescription({ children }: { children: ReactNode }) {
  const { descriptionId } = useField()
  return (
    <p id={descriptionId} className="text-xs text-text-muted">
      {children}
    </p>
  )
}

export function FieldError({ children }: { children: ReactNode }) {
  const { errorId } = useField()
  return (
    <p
      id={errorId}
      // Announced when it appears, so a validation failure is not silent for a
      // screen-reader user who has already moved on to the next field.
      role="alert"
      className="flex items-start gap-1.5 text-xs text-[var(--status-fail)]"
    >
      <AlertCircle size={13} className="mt-px shrink-0" aria-hidden />
      {children}
    </p>
  )
}
